/**
 * Fee-schedule client — the Fee Schedule model (migration 0071, Slices 1-3).
 *
 * Backend router: app/routers/fee_schedules.py, prefix /fee-schedules.
 *   - GET  /standard, /standard/history, POST /standard        the firm Standard
 *   - GET  /custom, POST /custom, PUT /custom/{id}              Custom schedules —
 *         DIRECT Admin write (maker-checker RETIRED with the Fee Schedule model,
 *         C-244/C-190-superseded); named + owned by one agreement, but usable by
 *         any agreement it's made AVAILABLE to (see availability below).
 *   - GET  /custom/available/{agreement_id}                     the schedules an
 *         agreement may be assigned (Standard + available_to_all/granted Custom)
 *   - GET/POST/DELETE /{id}/basis/...                           the AuM-basis
 *         group (source agreements + include/exclude overrides), tranches only
 *   - GET/PUT/POST/DELETE /{id}/availability/...                which agreements
 *         may use the schedule (firm-wide flag + explicit grants); revoke 409s
 *         if the agreement still has accounts assigned (design §3.7)
 *
 * Money is integer euro-cents, rates basis points — convert at this boundary
 * with the helpers below. "Current standard" is derived from the history (the
 * active version) so the UI needs no 404 handling — an empty history simply
 * means none defined yet.
 */
import { api } from './client';
import type { ISODate, ISODateTime, UUID } from './types';

export type FeeKind = 'fixed' | 'flat' | 'tranches';
export type FeeFrequency = 'monthly' | 'quarterly' | 'semiannual';
export type BasisOverrideMode = 'include' | 'exclude';

/** One progressive band on input. max_eur_cents=null is the open-top band. */
export interface FeeTrancheIn {
  min_eur_cents: number;
  max_eur_cents: number | null;
  rate_bps: number;
}

export interface FeeTrancheOut {
  seq: number;
  min_eur_cents: number;
  max_eur_cents: number | null;
  rate_bps: number;
}

export interface StandardScheduleCreate {
  kind: FeeKind;
  fixed_amount_cents?: number | null;
  flat_rate_bps?: number | null;
  bands?: FeeTrancheIn[] | null;
  effective_from?: ISODate | null;
}

/** Define (or edit) a Custom Fee Schedule — DIRECT Admin write, audited.
 *  Firm-owned (2026-07-01 redesign): `agreement_id` is OPTIONAL — omit it for a
 *  firm-owned schedule (availability is the sole scoping axis). `fee_frequency` is
 *  OPTIONAL too — the billing cadence is now per-ACCOUNT (on the Billing-details
 *  assignment, beside VAT), not per-schedule. Both kept optional for the transition. */
export interface CustomScheduleCreate {
  agreement_id?: UUID | null;
  name: string;
  description?: string | null;
  kind: FeeKind;
  fee_frequency?: FeeFrequency | null;
  bands?: FeeTrancheIn[] | null;
  fixed_amount_cents?: number | null;
  flat_rate_bps?: number | null;
  effective_from?: ISODate | null;
}

export interface CustomScheduleUpdate {
  name: string;
  description?: string | null;
  kind: FeeKind;
  fee_frequency?: FeeFrequency | null;
  bands?: FeeTrancheIn[] | null;
  fixed_amount_cents?: number | null;
  flat_rate_bps?: number | null;
  effective_from?: ISODate | null;
}

export interface FeeScheduleOut {
  id: UUID;
  scope: string;                       // 'standard' | 'custom' | 'crystallized'
  agreement_id: UUID | null;
  version: number;
  status: string;                      // 'draft' | 'active' | 'superseded'
  kind: FeeKind;
  fixed_amount_cents: number | null;
  flat_rate_bps: number | null;
  effective_from: ISODate | null;
  // Fee Schedule model (migration 0071) — nullable on legacy rows (pre-0071 / some Standard versions).
  name: string | null;
  description: string | null;
  fee_frequency: FeeFrequency | null;
  available_to_all: boolean;
  tranches: FeeTrancheOut[];
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
}

// ── AuM-basis group (Slice 3b) ──────────────────────────────────────────────
export interface BasisSourceOut { agreement_id: UUID; code: string | null; mnemonic: string | null }
export interface BasisOverrideOut { account_id: UUID; agreement_id: UUID | null; agreement_code: string | null; agreement_mnemonic: string | null; mode: BasisOverrideMode; label: string | null }
export interface BasisMemberOut { account_id: UUID; label: string | null }

/** One selectable account under an agreement in the #4b basis "Choose accounts"
 *  tree — an agreement's DIRECT + INDIRECT (via-policy) alive accounts. */
export interface BasisCandidateAccount {
  account_id: UUID;
  label: string;
  is_direct: boolean;
  status: string;
}
export interface BasisGroupOut {
  schedule_id: UUID;
  sources: BasisSourceOut[];
  overrides: BasisOverrideOut[];
  members: BasisMemberOut[];
}

// ── Availability (Slice 3c) ─────────────────────────────────────────────────
export interface AvailabilityOut {
  schedule_id: UUID;
  available_to_all: boolean;
  agreements: BasisSourceOut[];
}

/** An agreement relying on a schedule's 'available to all' umbrella (Slice-4 #5):
 *  assigned accounts but no explicit grant. The Edit UI auto-stages a grant for
 *  each when the admin turns the flag off, so nobody loses access by default. */
export interface AvailabilityDependentOut {
  agreement_id: UUID;
  code: string | null;
  mnemonic: string | null;
  account_count: number;
}

// ── unit converters (cents/bps live only behind this boundary) ──────────────
export const eurToCents = (eur: number): number => Math.round(eur * 100);
export const centsToEur = (cents: number): number => cents / 100;
export const pctToBps = (pct: number): number => Math.round(pct * 100);
export const bpsToPct = (bps: number): number => bps / 100;

/** €X formatted (from cents) — e.g. 1234567 → "12,345.67". */
export const fmtEurFromCents = (cents: number): string =>
  centsToEur(cents).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** P22 fee calculator: a schedule worked through at one amount (backend
 *  services/fee_example.py — the engine's own maths, HALF-UP, fee then VAT). */
export interface FeeExampleOut {
  schedule_id: UUID;
  schedule_label: string;
  kind: FeeKind;
  amount_cents: number;
  vat_rate_bps: number;
  lines: { min_cents: number; max_cents: number | null; rate_bps: number;
    portion_cents: number; fee_cents: number }[];
  annual_fee_cents: number;
  effective_rate_pct: number | null;
  periods: { frequency: FeeFrequency; periods_per_year: number; net_fee_cents: number;
    vat_cents: number; total_fee_cents: number }[];
}

export const feeSchedulesApi = {
  /** P22: price a schedule at an amount in euros (default €1,000,000 server-side). */
  /** P22 — the billing frequency picked (Pedro 2026-10-10), or several (2026-10-11: an
   *  agreement's simulation shows every frequency its accounts use; an empty list = all
   *  three). `vatRateBps` omitted = the server default (23%); an agreement passes its own. */
  example: (scheduleId: UUID, amountEur: number, frequency: FeeFrequency | FeeFrequency[],
    vatRateBps?: number | null) =>
    api.get<FeeExampleOut>(
      `/fee-schedules/${scheduleId}/example?amount_eur=${amountEur}`
      + (Array.isArray(frequency) ? frequency : [frequency]).map((f) => `&frequency=${f}`).join('')
      + (vatRateBps != null ? `&vat_rate_bps=${vatRateBps}` : '')),
  /** P22: the blank Annex I with this example — a same-origin GET (session cookie). */
  examplePdfHref: (scheduleId: UUID, amountEur: number, language: 'PT' | 'EN', frequency: FeeFrequency) =>
    `/fee-schedules/${scheduleId}/example.pdf?amount_eur=${amountEur}&language=${language}&frequency=${frequency}`,
  standardHistory: () => api.get<FeeScheduleOut[]>('/fee-schedules/standard/history'),
  createStandard: (b: StandardScheduleCreate) => api.post<FeeScheduleOut>('/fee-schedules/standard', b),

  /** All Custom schedules (Admin → Fees → Custom Fee Schedules tab). */
  listCustom: () => api.get<FeeScheduleOut[]>('/fee-schedules/custom'),
  /** The schedules an agreement may be assigned: the firm Standard(s) + any
   *  Custom available_to_all or granted to it (the Billing-details picker). */
  availableForAgreement: (agreementId: UUID) =>
    api.get<FeeScheduleOut[]>(`/fee-schedules/custom/available/${agreementId}`),
  /** Define a new Custom schedule — DIRECT Admin write. */
  createCustom: (b: CustomScheduleCreate) => api.post<FeeScheduleOut>('/fee-schedules/custom', b),
  /** Edit a Custom schedule in place (reference-live — reaches every account
   *  on it at the next compute). DIRECT Admin write. */
  updateCustom: (scheduleId: UUID, b: CustomScheduleUpdate) =>
    api.put<FeeScheduleOut>(`/fee-schedules/custom/${scheduleId}`, b),
  /** Clone a Custom schedule (copies definition + AuM-basis, NOT availability or
   *  assignments); the copy is firm-owned, name suffixed "(copy)" (Slice-4 #8). */
  cloneCustom: (scheduleId: UUID) => api.post<FeeScheduleOut>(`/fee-schedules/custom/${scheduleId}/clone`, {}),
  /** Deprecate (retire) a Custom schedule — soft-delete, kept for history; 409 if
   *  any account still assigns it (reassign first) (Slice-4 #8). */
  deprecateCustom: (scheduleId: UUID) => api.post<void>(`/fee-schedules/custom/${scheduleId}/deprecate`, {}),

  getBasis: (scheduleId: UUID) => api.get<BasisGroupOut>(`/fee-schedules/${scheduleId}/basis`),
  /** An agreement's DIRECT + INDIRECT alive accounts, for the #4b "Choose accounts"
   *  tri-state tree (agreement-scoped, not schedule-scoped). */
  basisCandidates: (agreementId: UUID) =>
    api.get<BasisCandidateAccount[]>(`/fee-schedules/basis-candidates/${agreementId}`),
  addBasisSource: (scheduleId: UUID, agreementId: UUID) =>
    api.post<BasisGroupOut>(`/fee-schedules/${scheduleId}/basis/sources`, { agreement_id: agreementId }),
  removeBasisSource: (scheduleId: UUID, agreementId: UUID) =>
    api.delete<BasisGroupOut>(`/fee-schedules/${scheduleId}/basis/sources/${agreementId}`),
  setBasisOverride: (scheduleId: UUID, accountId: UUID, mode: BasisOverrideMode) =>
    api.post<BasisGroupOut>(`/fee-schedules/${scheduleId}/basis/overrides`, { account_id: accountId, mode }),
  removeBasisOverride: (scheduleId: UUID, accountId: UUID) =>
    api.delete<BasisGroupOut>(`/fee-schedules/${scheduleId}/basis/overrides/${accountId}`),

  getAvailability: (scheduleId: UUID) => api.get<AvailabilityOut>(`/fee-schedules/${scheduleId}/availability`),
  /** Agreements relying on this schedule's 'available to all' umbrella — the Edit
   *  UI auto-stages a grant for each when the admin turns the flag off (Slice-4 #5). */
  availabilityDependents: (scheduleId: UUID) =>
    api.get<AvailabilityDependentOut[]>(`/fee-schedules/${scheduleId}/availability/dependents`),
  setAvailableToAll: (scheduleId: UUID, availableToAll: boolean) =>
    api.put<AvailabilityOut>(`/fee-schedules/${scheduleId}/availability/all`, { available_to_all: availableToAll }),
  grantAvailability: (scheduleId: UUID, agreementId: UUID) =>
    api.post<AvailabilityOut>(`/fee-schedules/${scheduleId}/availability/agreements`, { agreement_id: agreementId }),
  /** 409 if the agreement still has accounts assigned to this schedule —
   *  reassign first (design §3.7). Caller shows the 409 detail as a friendly message. */
  revokeAvailability: (scheduleId: UUID, agreementId: UUID) =>
    api.delete<AvailabilityOut>(`/fee-schedules/${scheduleId}/availability/agreements/${agreementId}`),
};
